import type { InputHTMLAttributes } from 'react';
import { forwardRef, useId } from 'react';
import { stripHandlePrefix } from '../utils/formatters';
import './HandleInput.css';

interface HandleInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  /** Current handle value (without "@") */
  value: string;
  /** Receives the cleaned handle (no "@" characters) */
  onChange: (value: string) => void;
  /** Optional label rendered above the input */
  label?: string;
  /** Validation error message rendered below the input */
  error?: string;
  /**
   * Visual variant. "submission" matches the public PlayerSubmissionForm look,
   * "dashboard" matches the dashboard TextInput look.
   */
  variant?: 'submission' | 'dashboard';
  /** Class applied to the outer wrapper (in addition to variant defaults) */
  wrapperClassName?: string;
}

/**
 * Handle/username input that displays a non-editable "@" prefix and prevents
 * the user from entering "@" characters. Stored value is always the raw handle.
 */
export const HandleInput = forwardRef<HTMLInputElement, HandleInputProps>(
  (
    {
      value,
      onChange,
      label,
      error,
      variant = 'submission',
      wrapperClassName = '',
      className = '',
      placeholder,
      disabled,
      id: idProp,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = idProp ?? generatedId;
    const errorId = `${inputId}-error`;
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(stripHandlePrefix(e.target.value));
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData('text');
      if (pasted.includes('@')) {
        e.preventDefault();
        const target = e.currentTarget;
        const start = target.selectionStart ?? value.length;
        const end = target.selectionEnd ?? value.length;
        const cleaned = stripHandlePrefix(pasted);
        const next = value.slice(0, start) + cleaned + value.slice(end);
        onChange(next);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === '@') e.preventDefault();
    };

    const variantClass =
      variant === 'dashboard' ? 'handle-input--dashboard' : 'handle-input--submission';
    const errorClass = error ? 'handle-input--error' : '';

    return (
      <div className={`handle-input ${variantClass} ${wrapperClassName}`}>
        {label && (
          <label className="handle-input__label" htmlFor={inputId}>
            {label}
          </label>
        )}
        <div className={`handle-input__wrapper ${errorClass}`}>
          <span className="handle-input__prefix" aria-hidden="true">
            @
          </span>
          <input
            ref={ref}
            id={inputId}
            type="text"
            className={`handle-input__field ${className}`}
            value={value}
            onChange={handleChange}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            {...rest}
          />
        </div>
        {error && (
          <span className="handle-input__error" id={errorId}>
            {error}
          </span>
        )}
      </div>
    );
  }
);

HandleInput.displayName = 'HandleInput';
